import { types, type Instance } from 'mobx-state-tree';

import { type Bucket, BUCKETS } from '../JobApplicationStore/types';

export enum BUCKET_COUNTS {
  INBOX_COUNT = 'inboxCount',
  PRESCREEN_COUNT = 'prescreenCount',
  SHORTLIST_COUNT = 'shortlistCount',
  INTERVIEW_COUNT = 'interviewCount',
  OFFER_COUNTS = 'offerCount',
  ACCEPT_COUNT = 'acceptCount',
  NOT_SUITABLE_COUNT = 'notSuitableCount',
}

const bucketNameToCountName = (bucket: BUCKETS) => {
  switch (bucket) {
    case BUCKETS.INBOX:
      return BUCKET_COUNTS.INBOX_COUNT;
    case BUCKETS.PRESCREEN:
      return BUCKET_COUNTS.PRESCREEN_COUNT;
    case BUCKETS.SHORTLIST:
      return BUCKET_COUNTS.SHORTLIST_COUNT;
    case BUCKETS.INTERVIEW:
      return BUCKET_COUNTS.INTERVIEW_COUNT;
    case BUCKETS.OFFER:
      return BUCKET_COUNTS.OFFER_COUNTS;
    case BUCKETS.ACCEPT:
      return BUCKET_COUNTS.ACCEPT_COUNT;
    case BUCKETS.NOT_SUITABLE:
      return BUCKET_COUNTS.NOT_SUITABLE_COUNT;

    default:
      throw new Error('Unknown bucket name');
  }
};

const BucketStats = types
  .model('BucketStats', {
    inboxCount: types.optional(types.number, 0),
    prescreenCount: types.optional(types.number, 0),
    shortlistCount: types.optional(types.number, 0),
    interviewCount: types.optional(types.number, 0),
    offerCount: types.optional(types.number, 0),
    acceptCount: types.optional(types.number, 0),
    notSuitableCount: types.optional(types.number, 0),
    newCount: types.optional(types.number, 0),
    totalCount: types.optional(types.number, 0),
  })
  .views((self) => ({
    get areAllApplicationsUnsuitable() {
      return (
        self.inboxCount === 0 &&
        self.prescreenCount === 0 &&
        self.shortlistCount === 0 &&
        self.interviewCount === 0 &&
        self.offerCount === 0 &&
        self.notSuitableCount > 0
      );
    },
    get hasPotentiallySuitableCandidates() {
      return (
        self.inboxCount === 0 &&
        (self.prescreenCount > 0 ||
          self.shortlistCount > 0 ||
          self.interviewCount > 0 ||
          self.offerCount > 0 ||
          self.acceptCount > 0)
      );
    },
    get hasNoCandidates() {
      return (
        self.inboxCount === 0 &&
        self.prescreenCount === 0 &&
        self.shortlistCount === 0 &&
        self.interviewCount === 0 &&
        self.offerCount === 0 &&
        self.acceptCount === 0 &&
        self.notSuitableCount === 0
      );
    },
  }))
  .actions((self) => {
    const moveBucket = (
      fromBucket: Bucket | string,
      toBucket: Bucket | string,
      count = 1,
    ) => {
      // TODO_TYPES: remove | string here. tighten up
      // @ts-ignore
      self[bucketNameToCountName(fromBucket)] -= count;
      // @ts-ignore
      self[bucketNameToCountName(toBucket)] += count;
    };

    const incrementBucketCount = (bucket: Bucket, count: number = 1) => {
      // @ts-ignore
      self[bucketNameToCountName(bucket)] += count;
    };

    const decrementNewCount = (numberToReduce = 1) => {
      const newCount = self.newCount - numberToReduce;
      self.newCount = newCount < 0 ? 0 : newCount;
    };

    const getBucketCount = (bucket: BUCKETS): number =>
      self[bucketNameToCountName(bucket)];

    const incrementTotalCount = () => {
      self.totalCount += 1;
    };
    return {
      moveBucket,
      incrementBucketCount,
      decrementNewCount,
      getBucketCount,
      incrementTotalCount,
    };
  });

export interface IBucketStats extends Instance<typeof BucketStats> {}

export default BucketStats;
